import { inject, Injectable, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { SocketRoomTicketService } from '@lite-workspace/utils';
import type { ProjectJourneyEvent } from '@lite-workspace/dto-fotoclass-common';
import type { ProjectJourneySocketPort } from '@lite-workspace/admin-project-journey/domain';

/** Server-side room prefix for the admin project-journey feed. */
export const PROJECT_JOURNEY_ROOM_PREFIX = 'project:journey:operations:';

const PROJECT_JOURNEY_SOCKET_EVENT = 'projectJourney:event';

interface RoomSubject {
  roomId: string;
  events$: Subject<ProjectJourneyEvent>;
  refs: number;
}

/** Thin admin-app Socket.IO client — trimmed from fotoclass-app's `core/socket.service.ts` to just
 *  what admin needs (room join/leave + one typed event stream), keeping the reconnect signal it relies on. */
@Injectable({ providedIn: 'root' })
export class SocketService implements ProjectJourneySocketPort {
  private readonly roomTickets = inject(SocketRoomTicketService);
  private readonly socket: Socket;
  private readonly subjects: RoomSubject[] = [];
  private wasConnected = false;
  private readonly reconnectedSubject = new Subject<void>();

  private readonly connectedSignal = signal(false);
  readonly connected = this.connectedSignal.asReadonly();
  /** Emits on every re-connect after a drop (not on the first connect) — reconciliation hook. */
  readonly reconnected$ = this.reconnectedSubject.asObservable();

  constructor() {
    this.socket = io('/', {
      path: '/socket.io',
      transports: ['websocket'],
      autoConnect: false,
    });

    this.socket.on('connect', () => {
      for (const s of this.subjects) {
        this.emitJoin(s.roomId);
      }

      this.connectedSignal.set(true);
      if (this.wasConnected) this.reconnectedSubject.next();
      this.wasConnected = true;
    });

    this.socket.on('disconnect', () => {
      this.connectedSignal.set(false);
    });

    // Wire shape is backend-libs' ProjectJourneyData: { roomId: <bare projectId>, event } —
    // the room is derived from it the same way it was joined.
    this.socket.on(PROJECT_JOURNEY_SOCKET_EVENT, (payload: { roomId: string; event: ProjectJourneyEvent }) => {
      const roomId = PROJECT_JOURNEY_ROOM_PREFIX + payload.roomId;
      const entry = this.subjects.find(s => s.roomId === roomId);
      entry?.events$.next(payload.event);
    });
  }

  connect(): void {
    if (!this.socket.connected) {
      this.socket.connect();
    }
  }

  // Room subjects are reference-counted: two consumers of one room share the subject, and a
  // leaveRoom from one must not complete the stream under the other.
  joinRoom(projectId: string): Observable<ProjectJourneyEvent> {
    const roomId = PROJECT_JOURNEY_ROOM_PREFIX + projectId;

    const existing = this.subjects.find(s => s.roomId === roomId);
    if (existing) {
      existing.refs++;
      return existing.events$.asObservable();
    }

    const entry: RoomSubject = { roomId, events$: new Subject<ProjectJourneyEvent>(), refs: 1 };
    this.subjects.push(entry);
    // Not connected yet: the connect handler joins every listed room, a join here would be the second one.
    if (this.socket.connected) this.emitJoin(roomId);

    return entry.events$.asObservable();
  }

  // The join carries a ticket the server minted for this room on an authenticated request. When the
  // mint fails the join still goes out ticketless: the server is the one that decides, and dropping
  // the join here would silently kill a subscription the server would have accepted.
  private emitJoin(roomId: string): void {
    this.roomTickets.requestTicket(roomId).subscribe(ticket => {
      const stillJoined = this.subjects.some(s => s.roomId === roomId);
      if (!stillJoined) return;

      this.socket.emit('joinUserToRoom', { roomId, ticket });
    });
  }

  leaveRoom(projectId: string): void {
    const roomId = PROJECT_JOURNEY_ROOM_PREFIX + projectId;
    const index = this.subjects.findIndex(s => s.roomId === roomId);
    if (index === -1) return;

    const entry = this.subjects[index];
    entry.refs--;
    if (entry.refs > 0) return;

    entry.events$.complete();
    this.subjects.splice(index, 1);
    this.socket.emit('leaveUserFromRoom', { roomId });
  }
}
